import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  PlaylistAddCheck as ActionItemIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useTaskOverlay } from '../../utils/TaskOverlayContext';

const EMPTY_RESULTS = { tasks: [], action_items: [] };

/**
 * @typedef {Object} SearchDialogProps
 * @property {boolean} open - Whether the dialog is open
 * @property {function} onClose - Called when the dialog should close
 */

/**
 * Global search dialog launched from the app bar. Searches tasks (name + notes)
 * and action items (description + notes) within the current workspace and shows
 * the results grouped. Selecting a result opens the relevant task detail.
 * @component
 * @param {SearchDialogProps} props - Component props
 * @returns {React.ReactElement} The search dialog
 */
const SearchDialog = ({ open, onClose }) => {
  const { currentWorkspace } = useWorkspace();
  const { openTaskDetail } = useTaskOverlay();

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(EMPTY_RESULTS);

  // Reset state whenever the dialog is closed so it opens clean next time.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(EMPTY_RESULTS);
      setLoading(false);
    }
  }, [open]);

  // Debounced search against the workspace-scoped search endpoint.
  useEffect(() => {
    if (!open) return undefined;

    const term = query.trim();
    if (!term || !currentWorkspace?.id) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const response = await api('search/', {
          params: { workspace_id: currentWorkspace.id, q: term },
        });
        setResults(response.data?.payload || EMPTY_RESULTS);
      } catch (error) {
        console.error('Search failed:', error);
        setResults(EMPTY_RESULTS);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [query, open, currentWorkspace?.id]);

  // Open the task detail surface for the given task and close the dialog.
  const handleOpenTask = (taskId) => {
    if (taskId == null) return;
    onClose();
    openTaskDetail(taskId);
  };

  const term = query.trim();
  const tasks = results.tasks || [];
  const actionItems = results.action_items || [];
  const hasResults = tasks.length > 0 || actionItems.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { position: 'fixed', top: 80, m: 0 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search tasks, action items and notes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Divider />

        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {!term && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 3, textAlign: 'center' }}
            >
              Start typing to search across your workspace.
            </Typography>
          )}

          {term && !loading && !hasResults && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 3, textAlign: 'center' }}
            >
              No results for &ldquo;{term}&rdquo;.
            </Typography>
          )}

          {tasks.length > 0 && (
            <List dense disablePadding>
              <Typography
                variant="overline"
                sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary', fontWeight: 700 }}
              >
                Tasks
              </Typography>
              {tasks.map((task) => (
                <ListItemButton key={`task-${task.id}`} onClick={() => handleOpenTask(task.id)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <TaskIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={task.name}
                    primaryTypographyProps={{ noWrap: true }}
                    secondary={[task.track_name, task.status].filter(Boolean).join(' · ')}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          {actionItems.length > 0 && (
            <List dense disablePadding>
              <Typography
                variant="overline"
                sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary', fontWeight: 700 }}
              >
                Action Items
              </Typography>
              {actionItems.map((item) => (
                <ListItemButton
                  key={`ai-${item.id}`}
                  onClick={() => handleOpenTask(item.task_id)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ActionItemIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.description}
                    primaryTypographyProps={{ noWrap: true }}
                    secondary={item.task_name}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                  {item.is_completed && (
                    <Chip label="Done" size="small" color="success" variant="outlined" />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
